/**
 * `/compare/<slug>` — public "Bystep vs X" comparison pages (moat plan §6.3, SEO/content channel).
 * Same `.mk` vocabulary as `/integrations`: light-only, no shadcn primitives inside `.mk`.
 * Data lives in `#/lib/compare.ts`; an unknown slug 404s via TanStack's `notFound()`.
 */
import { createFileRoute, notFound } from '@tanstack/react-router'
import { Check, Minus } from 'lucide-react'
import { Container, CtaPair, MkFooter, MkLink, MkNav, Overline, SectionHead } from '#/components/landing/mk'
import { BYSTEP_MATRIX, getCompetitor, ROW_IDS } from '#/lib/compare'
import type { Competitor, MatrixValue, RowId } from '#/lib/compare'
import { m } from '#/paraglide/messages'
import { getLocale } from '#/paraglide/runtime'

export const Route = createFileRoute('/compare/$slug')({
  loader: ({ params }) => {
    const competitor = getCompetitor(params.slug)
    if (!competitor) throw notFound()
    return competitor
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {}
    const name = loaderData.name
    return {
      meta: [
        { title: m.cmp_meta_title({ name }) },
        { name: 'description', content: m.cmp_meta_desc({ name }) },
        { property: 'og:title', content: m.cmp_meta_title({ name }) },
        { property: 'og:description', content: m.cmp_meta_desc({ name }) },
        { property: 'og:type', content: 'website' },
      ],
    }
  },
  notFoundComponent: NotFoundPage,
  component: ComparePage,
})

const ROW_LABEL: Record<RowId, () => string> = {
  prd: () => m.cmp_row_prd(),
  ac: () => m.cmp_row_ac(),
  gate: () => m.cmp_row_gate(),
  evidence: () => m.cmp_row_evidence(),
  agent: () => m.cmp_row_agent(),
  importexport: () => m.cmp_row_importexport(),
  client: () => m.cmp_row_client(),
  cost: () => m.cmp_row_cost(),
  idlocal: () => m.cmp_row_idlocal(),
  oss: () => m.cmp_row_oss(),
}

function Mark({ v }: { v: MatrixValue }) {
  if (v === 'yes')
    return (
      <span className="inline-flex items-center justify-center">
        <Check className="size-[18px] text-[var(--mk-accent)]" aria-hidden />
        <span className="sr-only">{m.cmp_yes()}</span>
      </span>
    )
  if (v === 'no')
    return (
      <span className="inline-flex items-center justify-center">
        <Minus className="size-[18px] text-[var(--mk-fg-faint)]" aria-hidden />
        <span className="sr-only">{m.cmp_no()}</span>
      </span>
    )
  return (
    <span className="inline-flex items-center justify-center">
      <span aria-hidden className="text-[15px] leading-none text-[var(--mk-fg-muted)]">
        ◐
      </span>
      <span className="sr-only">{m.cmp_partial()}</span>
    </span>
  )
}

function Hero({ c }: { c: Competitor }) {
  return (
    <section className="pt-16 pb-10 lg:pt-24 lg:pb-14">
      <Container>
        <SectionHead
          overline={m.cmp_hero_overline()}
          title={m.cmp_hero_title({ name: c.name })}
          lead={getLocale() === 'id' ? c.what.id : c.what.en}
          actions={
            c.noImport ? (
              <CtaPair primary={m.cmp_hero_cta_primary()} secondary={m.cmp_hero_cta_secondary_agents()} secondaryHref="/#agent-platform" />
            ) : (
              <CtaPair primary={m.cmp_hero_cta_primary()} secondary={m.cmp_hero_cta_secondary({ name: c.name })} secondaryTo="/integrations" />
            )
          }
        />
      </Container>
    </section>
  )
}

function About({ c }: { c: Competitor }) {
  const locale = getLocale()
  return (
    <section className="pb-14 lg:pb-20">
      <Container>
        <div className="mk-card-white p-6 sm:p-8">
          <Overline>{m.cmp_about_overline({ name: c.name })}</Overline>
          <p className="mk-lead mt-3 max-w-[64ch]">{locale === 'id' ? c.what.id : c.what.en}</p>
          <dl className="mt-6 grid gap-6 sm:grid-cols-2">
            <div>
              <dt className="text-sm text-[var(--mk-fg-muted)]">{m.cmp_about_pricing_label()}</dt>
              <dd className="mt-1 text-[15px] text-[var(--mk-fg)]">{locale === 'id' ? c.pricing.id : c.pricing.en}</dd>
            </div>
            <div className="flex items-end">
              <a href={c.url} target="_blank" rel="noopener noreferrer nofollow" className="text-[15px] text-[var(--mk-accent)] hover:underline">
                {m.cmp_about_visit({ name: c.name })}
              </a>
            </div>
          </dl>
          {c.verified ? (
            <p className="mt-6 border-t border-[var(--mk-border)] pt-4 text-sm text-[var(--mk-fg-muted)]" data-testid="compare-verified">
              {m.cmp_about_verified({ date: locale === 'id' ? c.verified.date.id : c.verified.date.en })}
              {' · '}
              {m.cmp_about_sources()}:{' '}
              {c.verified.sources.map((src, i) => (
                <span key={src}>
                  {i > 0 ? ', ' : null}
                  <a href={src} target="_blank" rel="noopener noreferrer nofollow" className="break-words underline underline-offset-4 hover:text-[var(--mk-fg)]">
                    {src.replace(/^https:\/\//, '').replace(/\/$/, '')}
                  </a>
                </span>
              ))}
            </p>
          ) : null}
        </div>
      </Container>
    </section>
  )
}

function Matrix({ c }: { c: Competitor }) {
  const locale = getLocale()
  return (
    <section className="py-14 lg:py-20">
      <Container>
        <SectionHead align="left" overline={m.cmp_matrix_overline()} title={m.cmp_matrix_title()} lead={m.cmp_matrix_lead()} />
        <div className="mk-card-white mt-12 overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-left text-[15px]">
            <thead>
              <tr className="border-b border-[var(--mk-border)] text-sm text-[var(--mk-fg-muted)]">
                <th scope="col" className="px-6 py-4 font-medium">
                  {' '}
                </th>
                <th scope="col" className="px-4 py-4 text-center font-medium">
                  {m.cmp_col_bystep()}
                </th>
                <th scope="col" className="px-4 py-4 text-center font-medium">
                  {c.name}
                </th>
              </tr>
            </thead>
            <tbody>
              {ROW_IDS.map((id) => {
                const b = BYSTEP_MATRIX[id]
                const o = c.matrix[id]
                return (
                  <tr key={id} className="border-b border-[var(--mk-border)] last:border-b-0 align-top">
                    <th scope="row" className="px-6 py-4 font-normal">
                      <span className="font-medium text-[var(--mk-fg)]">{ROW_LABEL[id]()}</span>
                    </th>
                    <td className="px-4 py-4 text-center">
                      <Mark v={b.v} />
                      {b.note ? <span className="mt-1 block text-[12px] text-[var(--mk-fg-faint)]">{locale === 'id' ? b.note.id : b.note.en}</span> : null}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <Mark v={o.v} />
                      {o.note ? <span className="mt-1 block text-[12px] text-[var(--mk-fg-faint)]">{locale === 'id' ? o.note.id : o.note.en}</span> : null}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Container>
    </section>
  )
}

function WhenToChoose({ c }: { c: Competitor }) {
  const locale = getLocale()
  return (
    <section className="mk-dark-section py-20 lg:py-28">
      <Container>
        <SectionHead align="left" overline={m.cmp_when_overline()} title={m.cmp_when_title({ name: c.name })} />
        <ul className="mt-10 grid gap-6 md:grid-cols-3">
          {c.whenToChoose.map((b, i) => (
            <li key={i} className="rounded-[12px] border border-[var(--mk-dark-border)] bg-[var(--mk-dark-card)] p-5 text-[15px] leading-relaxed text-[var(--mk-dark-fg)]">
              {locale === 'id' ? b.id : b.en}
            </li>
          ))}
        </ul>
      </Container>
    </section>
  )
}

function FinalCta({ c }: { c: Competitor }) {
  return (
    <section className="py-24 lg:py-32">
      <Container>
        {c.noImport ? (
          <SectionHead title={m.cmp_cta_title_agents()} lead={m.cmp_cta_lead_agents()} actions={<CtaPair primary={m.cmp_cta_primary()} secondary={m.cmp_hero_cta_secondary_agents()} secondaryHref="/#agent-platform" />} />
        ) : (
          <SectionHead title={m.cmp_cta_title()} lead={m.cmp_cta_lead()} actions={<CtaPair primary={m.cmp_cta_primary()} secondary={m.cmp_cta_secondary()} secondaryTo="/integrations" />} />
        )}
      </Container>
    </section>
  )
}

function ComparePage() {
  const c = Route.useLoaderData()
  return (
    <div className="mk h-full overflow-x-hidden overflow-y-auto scroll-smooth">
      <MkNav />
      <main>
        <Hero c={c} />
        <About c={c} />
        <Matrix c={c} />
        <WhenToChoose c={c} />
        <FinalCta c={c} />
      </main>
      <MkFooter />
    </div>
  )
}

function NotFoundPage() {
  return (
    <div className="mk h-full overflow-x-hidden overflow-y-auto scroll-smooth">
      <MkNav />
      <main>
        <section className="py-24 lg:py-32">
          <Container>
            <SectionHead
              title={m.cmp_404_title()}
              lead={m.cmp_404_lead()}
              actions={
                <MkLink to="/" className="h-10">
                  {m.cmp_404_cta()}
                </MkLink>
              }
            />
          </Container>
        </section>
      </main>
      <MkFooter />
    </div>
  )
}
